<script>
    import Transition from "../components/Transition.svelte";
    import ModalWindow from "../components/ModalWindow.svelte";
    import Material from "./Material.svelte";
    import {customFetch} from "../customFetch";
    import {materialsUrl, materialUrl} from "../urls";
    import {notifications} from "../notifications";
    import {onMount} from "svelte";
    import RoundWrapper from "../components/RoundWrapper.svelte";

    let isLoading = false;
    let tableData = [];

    let editingMaterial = null;
    let selectedRow = null;

    let shadersMap = {
        "Opaque": ["Albedo", "Metallic", "Emission", "Roughness"],
        "Transparent": ["Albedo", "Emission", "Roughness"],
        "TerrainBased": ["Albedo"],
        "NavTracks": ["Albedo"],
        "Blueprint": ["Albedo"],
    }

    onMount(async () => {
        isLoading = true;

        await load();

        isLoading = false;
    });

    const addNewMaterial = () => {
        let textures = [];
        for (let i = 0; i < 4; i++) textures.push({Name: "", Image: ""});

        editingMaterial = {ShaderName: "Opaque", Textures: textures};
    };

    async function selectRow(row) {
        if (!row) return;

        selectedRow = row;
        editingMaterial = await get(row.ID);
    }

    async function saveMaterial(event) {
        const material = event.detail;

        editingMaterial.Name = material.Name;
        editingMaterial.ShaderName = material.ShaderName;

        editingMaterial = null;

        await save(material);
        await load();
    }

    async function removeMaterial() {
        await remove();
        await load();
    }

    async function get(id) {
        try {
            let request = await customFetch(materialUrl(id));
            editingMaterial = await request.json();

            return editingMaterial;
        } catch (e) {
            notifications.danger(
                `Can't get material ${id} data from server: ${e.message}`
            );
            return null;
        }
    }

    async function load() {
        try {
            let response = await customFetch(materialsUrl());

            tableData = await response.json();
        } catch (e) {
            notifications.danger(
                `Can't get actual materials list from server: ${e.message}`
            );
        }
    }

    async function save(item) {
        let isNew = item.ID == null;

        let url = isNew ? materialsUrl() : materialUrl(item.ID);

        delete item.ID; // handling unmarshal error

        let request = {
            method: isNew ? "POST" : "PUT",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify(item),
        };

        try {
            if (item.ID) await customFetch(url, request);
            else await customFetch(url, request);

            editingMaterial = null;
        } catch (e) {
            notifications.danger(`Can't save material: ${e.message}`);
        }
    }

    async function remove() {
        let url = materialUrl(editingMaterial.ID);

        try {
            await customFetch(url, {method: "DELETE"});
        } catch (e) {
            notifications.danger(`Can't delete material: ${e.message}`);
        }

        editingMaterial = null;
    }

    function handleNav(delta) {
        let index = tableData.indexOf(selectedRow);
        index += delta;

        selectRow(tableData[index]);
    }

    function closeMaterial() {
        selectedRow = null;
        editingMaterial = null;
    }
</script>

{#if editingMaterial}
    <ModalWindow
        on:close={closeMaterial}
        header={editingMaterial.ID ? "Editing material" : "New material"}
        on:prev={() => handleNav(-1)}
        on:next={() => handleNav(+1)}
    >
        <Material
            on:submit={saveMaterial}
            on:remove={removeMaterial}
            on:close={closeMaterial}
            material={editingMaterial}
            {shadersMap}
        />
    </ModalWindow>
{/if}

<Transition>
    <RoundWrapper>
        {#if isLoading}
            <span class="animate-pulse">Loading...</span>
        {:else if tableData.length > 0}
            <table class="table-auto w-full">
                <thead>
                <tr>
                    <th>Name</th>
                    <th>Shader</th>
                    <th>Modified</th>
                </tr>
                <tr/>
                </thead>
                <tbody>
                {#each Object.values(tableData) as row, i}
                    <tr
                        class="striped select-none transition-colors {selectedRow === row ? 'selected':''}"
                        on:click={() => selectRow(row)}
                    >
                        <td>{row.Name}</td>
                        <td>{row.ShaderName}</td>
                        <td>{row.Modified ? "Yes" : "No"}</td>
                    </tr>
                {/each}
                </tbody>
            </table>
        {:else}
            There is nothing to show
        {/if}
        <div class="m-2">
            <button class="btn-primary" on:click={addNewMaterial}>Add new material</button>
        </div>
    </RoundWrapper>
</Transition>

<style>
    td, th {
        @apply px-2 py-1
    }

    td {
        @apply cursor-pointer
    }
</style>
